import { useRef, useState } from "react";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import UploadProgress from "@/components/UploadProgress";
import api, { fmtErr } from "@/lib/api";

const src = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export default function ReelManager() {
  const { user, setUser } = useAuth();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const profile = user?.profile || {};
  const reels = profile.reels || [];
  const thumbs = profile.reel_thumbs || [];

  const upload = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/profile/reels", fd, {
        onUploadProgress: (ev) => setProgress(Math.round((ev.loaded / (ev.total || ev.loaded)) * 100)),
      });
      setUser({ ...user, profile: { ...profile, reels: data.reels, reel_thumbs: data.reel_thumbs } });
      toast.success("Reel added to your book.");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
      setProgress(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (url) => {
    try {
      const { data } = await api.delete("/profile/reels", { data: { url } });
      setUser({ ...user, profile: { ...profile, reels: data.reels, reel_thumbs: data.reel_thumbs } });
      toast.success("Reel removed.");
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  return (
    <div className="panel p-8 mt-10" data-testid="reel-manager">
      <div className="flex items-center justify-between mb-8">
        <p className="overline-label !font-semibold !text-white/85">Demo reels ({reels.length})</p>
        <button data-testid="reel-upload-button" onClick={() => inputRef.current?.click()} disabled={busy} className="btn-outline !px-5 !py-2.5 disabled:opacity-40">
          <Upload size={14} /> {busy ? "Uploading…" : "Add reel"}
        </button>
        <input ref={inputRef} data-testid="reel-file-input" type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={upload} />
      </div>
      {busy && progress != null && <UploadProgress percent={progress} note="Uploading reel" />}
      {reels.length === 0 ? (
        <p className="font-display italic text-2xl text-white/40" data-testid="reels-empty">
          Stills stop them; motion books them — add a reel.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {reels.map((r, i) => (
            <div key={r} className="group relative bg-black" data-testid={`reel-${i}`}>
              <video src={src(r)} poster={thumbs[i] ? src(thumbs[i]) : undefined} controls preload="metadata" className="w-full aspect-video object-cover" />
              <button
                data-testid={`remove-reel-${i}`}
                onClick={() => remove(r)}
                className="absolute top-2 right-2 bg-background/80 border border-white/25 hover:border-accent hover:text-accent px-2 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.15em] transition-colors inline-flex items-center gap-1"
              >
                <X size={11} /> Remove
              </button>
            </div>
          ))}
        </div>
      )}
      <p className="mt-6 font-grotesk font-semibold text-[11px] uppercase tracking-[0.15em] text-white/70">MP4 · WebM · MOV — up to 200MB</p>
    </div>
  );
}
